import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES, type Role } from '@deployctl/shared';
import { apiFetch, ApiError } from '../../lib/api.js';
import { useSession } from '../../lib/useSession.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { StatusPill } from '../../components/ui/StatusPill.js';
import { useToast } from '../../components/ui/Toast.js';
import './users.css';

interface UserRow {
  id: string;
  username: string;
  email: string;
  displayName: string;
  role: Role;
  totpEnabled: boolean;
  totpRequired: boolean;
  isActive: boolean;
  lockedUntil: string | null;
  createdAt: string;
}

interface PatchUserBody {
  username?: string;
  email?: string;
  displayName?: string;
  role?: Role;
  isActive?: boolean;
  unlock?: true;
  totpRequired?: boolean;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

const PASSWORD_CHARSET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_=+';

function generatePassword(length = 20): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const b of bytes) out += PASSWORD_CHARSET.charAt(b % PASSWORD_CHARSET.length);
  return out;
}

function isLocked(u: UserRow): boolean {
  return Boolean(u.lockedUntil) && new Date(u.lockedUntil!).getTime() > Date.now();
}

function EditUserDialog({
  user,
  onClose,
  onSaved,
}: {
  user: UserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [username, setUsername] = useState(user.username);
  const [email, setEmail] = useState(user.email);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [totpRequired, setTotpRequired] = useState(user.totpRequired);

  const save = useMutation({
    mutationFn: () => {
      const body: { username?: string; email?: string; displayName?: string; totpRequired?: boolean } = {};
      if (username !== user.username) body.username = username;
      if (email !== user.email) body.email = email;
      if (displayName !== user.displayName) body.displayName = displayName;
      if (totpRequired !== user.totpRequired) body.totpRequired = totpRequired;
      return apiFetch<UserRow>(`/users/${user.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      toast.success(`"${user.username}" updated`);
      onSaved();
      onClose();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'failed to update user'),
  });

  return (
    <Dialog open onClose={onClose} title={`Edit ${user.username}`}>
      <form
        className="dialog-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!save.isPending) save.mutate();
        }}
      >
        <div className="field">
          <label htmlFor="edit-username">Username</label>
          <input
            id="edit-username"
            className="mono"
            autoComplete="off"
            maxLength={64}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="edit-email">Email</label>
          <input
            id="edit-email"
            type="email"
            className="mono"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="edit-displayname">Display name</label>
          <input
            id="edit-displayname"
            maxLength={80}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="check-row">
            <input
              type="checkbox"
              checked={totpRequired}
              onChange={(e) => setTotpRequired(e.target.checked)}
            />
            Require 2FA at login
          </label>
        </div>
        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-amber" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function UsersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: me } = useSession();

  const { data: users, isLoading } = useQuery<UserRow[]>({
    queryKey: ['users'],
    queryFn: () => apiFetch<UserRow[]>('/users'),
  });

  // ── create ──
  const [createOpen, setCreateOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('viewer');

  const resetCreateForm = () => {
    setUsername('');
    setEmail('');
    setDisplayName('');
    setPassword('');
    setRole('viewer');
  };

  const createMut = useMutation({
    mutationFn: (body: {
      username: string;
      email: string;
      displayName: string;
      password: string;
      role: Role;
    }) => apiFetch<UserRow>('/users', { method: 'POST', body }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setCreateOpen(false);
      resetCreateForm();
      toast.success(`User "${created.username}" created`);
    },
    onError: (err) => toast.error(errMessage(err, 'failed to create user')),
  });

  // ── patch (role / active / unlock) ──
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchUserBody }) =>
      apiFetch<UserRow>(`/users/${id}`, { method: 'PATCH', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => toast.error(errMessage(err, 'update failed')),
  });

  // ── edit user ──
  const [editing, setEditing] = useState<UserRow | null>(null);

  // ── reset password ──
  const [resetTarget, setResetTarget] = useState<UserRow | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const resetMut = useMutation({
    mutationFn: ({ id, password: pw }: { id: string; password: string }) =>
      apiFetch<{ ok: boolean }>(`/users/${id}/reset-password`, {
        method: 'POST',
        body: { password: pw },
      }),
    onSuccess: () => {
      toast.success(
        `Password reset for "${resetTarget?.username ?? 'user'}" — their sessions were revoked`,
      );
      setResetTarget(null);
      setResetPassword('');
    },
    onError: (err) => toast.error(errMessage(err, 'password reset failed')),
  });

  const changeRole = (u: UserRow, newRole: Role) => {
    patchMut.mutate(
      { id: u.id, body: { role: newRole } },
      { onSuccess: () => toast.success(`"${u.username}" is now ${newRole}`) },
    );
  };

  const toggleActive = (u: UserRow) => {
    patchMut.mutate(
      { id: u.id, body: { isActive: !u.isActive } },
      {
        onSuccess: () => toast.success(`"${u.username}" ${u.isActive ? 'disabled' : 'enabled'}`),
      },
    );
  };

  const unlock = (u: UserRow) => {
    patchMut.mutate(
      { id: u.id, body: { unlock: true } },
      { onSuccess: () => toast.success(`"${u.username}" unlocked`) },
    );
  };

  const columns: Column<UserRow>[] = [
    {
      key: 'user',
      header: 'User',
      render: (u) => (
        <div className="users-id">
          <span className="users-name">{u.displayName}</span>
          <span className="mono users-username">
            {u.username} · {u.email}
          </span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (u) => (
        <select
          className="mono users-role-select"
          value={u.role}
          disabled={u.id === me?.id || patchMut.isPending}
          onChange={(e) => changeRole(u, e.target.value as Role)}
          aria-label={`Role for ${u.username}`}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) =>
        !u.isActive ? (
          <StatusPill variant="err">disabled</StatusPill>
        ) : isLocked(u) ? (
          <StatusPill variant="warn">locked</StatusPill>
        ) : (
          <StatusPill variant="ok">active</StatusPill>
        ),
    },
    {
      key: 'totp',
      header: '2FA',
      render: (u) =>
        u.totpEnabled ? (
          <StatusPill variant="ok">on</StatusPill>
        ) : (
          <StatusPill variant="neutral">off</StatusPill>
        ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (u) => <span className="ui-cell-mute">{fmtDate(u.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (u) => (
        <div className="ui-cell-actions">
          {isLocked(u) ? (
            <button className="btn btn-ghost btn-sm" onClick={() => unlock(u)}>
              Unlock
            </button>
          ) : null}
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(u)}>
            Edit
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setResetPassword('');
              setResetTarget(u);
            }}
          >
            Reset password
          </button>
          <button
            className={`btn btn-sm ${u.isActive ? 'btn-ghost-danger' : 'btn-ghost'}`}
            disabled={u.id === me?.id}
            onClick={() => toggleActive(u)}
          >
            {u.isActive ? 'Disable' : 'Enable'}
          </button>
        </div>
      ),
    },
  ];

  const canCreate =
    username.trim().length > 0 &&
    email.trim().length > 3 &&
    displayName.trim().length > 0 &&
    password.length >= 12;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Users</h1>
          <p className="page-sub">Accounts, roles, and access control.</p>
        </div>
        <button className="btn btn-amber" onClick={() => setCreateOpen(true)}>
          Create user
        </button>
      </div>

      <Table
        columns={columns}
        rows={users ?? []}
        rowKey={(u) => u.id}
        empty={isLoading ? 'Loading…' : 'No users found.'}
      />

      {/* create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          resetCreateForm();
        }}
        title="Create user"
      >
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCreate && !createMut.isPending) {
              createMut.mutate({
                username: username.trim(),
                email: email.trim(),
                displayName: displayName.trim(),
                password,
                role,
              });
            }
          }}
        >
          <div className="field">
            <label htmlFor="new-username">Username</label>
            <input
              id="new-username"
              className="mono"
              autoFocus
              autoComplete="off"
              maxLength={64}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-email">Email</label>
            <input
              id="new-email"
              type="email"
              className="mono"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-displayname">Display name</label>
            <input
              id="new-displayname"
              maxLength={80}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="new-password">Temporary password (min 12 chars)</label>
            <div className="field-row">
              <input
                id="new-password"
                className="mono"
                autoComplete="new-password"
                spellCheck={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setPassword(generatePassword())}
              >
                Generate
              </button>
            </div>
          </div>
          <div className="field">
            <label htmlFor="new-role">Role</label>
            <select
              id="new-role"
              className="mono"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="dialog-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setCreateOpen(false);
                resetCreateForm();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-amber"
              disabled={!canCreate || createMut.isPending}
            >
              {createMut.isPending ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* edit user dialog */}
      {editing ? (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['users'] })}
        />
      ) : null}

      {/* reset password dialog */}
      <Dialog
        open={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        title={resetTarget ? `Reset password — ${resetTarget.username}` : 'Reset password'}
      >
        {resetTarget ? (
          <form
            className="dialog-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (resetPassword.length >= 12 && !resetMut.isPending) {
                resetMut.mutate({ id: resetTarget.id, password: resetPassword });
              }
            }}
          >
            <p className="dialog-desc">
              Sets a new password for{' '}
              <span className="mono confirm-target">{resetTarget.username}</span> and revokes all
              of their sessions.
            </p>
            <div className="field">
              <label htmlFor="reset-password">New password (min 12 chars)</label>
              <div className="field-row">
                <input
                  id="reset-password"
                  className="mono"
                  autoComplete="new-password"
                  spellCheck={false}
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setResetPassword(generatePassword())}
                >
                  Generate
                </button>
              </div>
            </div>
            <div className="dialog-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setResetTarget(null)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-amber"
                disabled={resetPassword.length < 12 || resetMut.isPending}
              >
                {resetMut.isPending ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </form>
        ) : null}
      </Dialog>
    </div>
  );
}
